/**
 * CRYIN — SPA Router
 * Intercepts internal navigation, fetches pages via AJAX,
 * and swaps content with View Transitions API for smooth
 * zoom-like transitions (no full page reload).
 */

(function () {
  'use strict';

  // Only run if View Transitions API is supported
  const supportsViewTransitions = Boolean(document.startViewTransition);

  // Cache fetched pages
  const pageCache = new Map();

  /**
   * Animate cart counter with bump class
   */
  function animateCartBump() {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.classList.remove('is-bumping');
      el.offsetHeight; // force reflow
      el.classList.add('is-bumping');
      el.addEventListener('animationend', () => el.classList.remove('is-bumping'), { once: true });
    });
  }

  /**
   * Fly-to-cart ghost animation
   * Creates a small ghost image of the product that flies to the cart icon (top-right)
   */
  function flyToCart() {
    const productImg = document.querySelector('.pdp__slide--active img');
    const cartTarget = document.querySelector('.header__cart');
    if (!productImg || !cartTarget) { animateCartBump(); return; }

    const imgRect = productImg.getBoundingClientRect();
    const cartRect = cartTarget.getBoundingClientRect();

    // Ghost starts as a small version of the product image at center of product
    const ghostSize = 80;
    const startX = imgRect.left + (imgRect.width - ghostSize) / 2;
    const startY = imgRect.top + (imgRect.height - ghostSize) / 2;

    const ghost = document.createElement('img');
    ghost.src = productImg.currentSrc || productImg.src;
    ghost.className = 'atc-ghost';
    ghost.style.cssText = `
      top: ${startY}px;
      left: ${startX}px;
      width: ${ghostSize}px;
      height: ${ghostSize}px;
    `;
    document.body.appendChild(ghost);

    // Force reflow, then fly to cart icon
    ghost.offsetHeight;
    const endX = cartRect.left + cartRect.width / 2 - ghostSize / 2;
    const endY = cartRect.top + cartRect.height / 2 - ghostSize / 2;
    const dx = endX - startX;
    const dy = endY - startY;
    ghost.style.transform = `translate(${dx}px, ${dy}px) scale(0.15)`;
    ghost.classList.add('is-flying');

    ghost.addEventListener('transitionend', () => {
      ghost.remove();
      animateCartBump();
    }, { once: true });

    // Safety cleanup
    setTimeout(() => { if (ghost.parentNode) ghost.remove(); }, 1200);
  }

  /**
   * Update all cart count elements and ensure they're visible
   */
  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
    });
    // If cart count element didn't exist yet, inject it in the header
    const cartLink = document.querySelector('.header__cart');
    if (cartLink && count > 0 && !cartLink.querySelector('[data-cart-count]')) {
      const span = document.createElement('span');
      span.className = 'header__cart-text';
      span.setAttribute('data-cart-count', '');
      span.textContent = count;
      cartLink.insertBefore(span, cartLink.firstChild);
    }
  }

  /**
   * Check if a URL is an internal navigable link
   */
  function isInternalLink(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      // Same origin, not an anchor, not a file download
      if (parsed.origin !== window.location.origin) return false;
      if (parsed.pathname === window.location.pathname && parsed.hash) return false;
      // Skip cart add/change URLs
      if (parsed.pathname.includes('/cart/add')) return false;
      if (parsed.pathname.includes('/cart/change')) return false;
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Fetch a page and extract content
   */
  async function fetchPage(url) {
    if (pageCache.has(url)) return pageCache.get(url);

    const response = await fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) throw new Error(`Failed to fetch ${url}`);

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const data = {
      title: doc.title,
      main: doc.querySelector('#main'),
      bodyClass: doc.body.className,
      // Extract any inline scripts from main content
      scripts: [...doc.querySelectorAll('#main script[src]')].map(s => s.src)
    };

    // Cache (limit size)
    if (pageCache.size > 20) {
      const firstKey = pageCache.keys().next().value;
      pageCache.delete(firstKey);
    }
    pageCache.set(url, data);

    return data;
  }

  /**
   * Swap page content
   */
  function swapContent(data) {
    const main = document.querySelector('#main');
    if (!main || !data.main) return;

    // Update title
    document.title = data.title;

    // Update body class (template class)
    document.body.className = data.bodyClass;

    // Swap main content
    main.innerHTML = data.main.innerHTML;

    // If we're navigating with a product zoom transition, set view-transition-name on the target
    // and suppress all card names so only the hero morphs
    if (clickedProductId) {
      // Suppress card transition names in new content for product-zoom
      main.querySelectorAll('.product-card').forEach(card => {
        card.style.viewTransitionName = 'none';
      });
      // Target could be: PDP first slide (going to product) or product card media (going back to grid)
      const pdpTarget = main.querySelector(`.pdp__slide[data-product-id="${clickedProductId}"]`);
      const gridTarget = main.querySelector(`.product-card__media[data-product-id="${clickedProductId}"]`);
      const target = pdpTarget || gridTarget;
      if (target) {
        target.style.viewTransitionName = 'product-hero';
        // Clean up after transition
        requestAnimationFrame(() => {
          setTimeout(() => {
            target.style.viewTransitionName = '';
            // Restore card names
            main.querySelectorAll('.product-card').forEach(card => {
              card.style.viewTransitionName = '';
            });
            clickedProductId = null;
          }, 500);
        });
      } else {
        clickedProductId = null;
        // Restore card names
        main.querySelectorAll('.product-card').forEach(card => {
          card.style.viewTransitionName = '';
        });
      }
    }

    // Re-initialize components in new content
    reinitComponents();

    // Execute new page scripts
    data.scripts.forEach(src => {
      // Check if script already loaded
      if (document.querySelector(`script[src="${src}"]`)) return;
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      document.body.appendChild(script);
    });

    // Update active nav item
    updateActiveNav(window.location.pathname);

    // Track collection URL for PDP back button
    trackCollectionUrl();

    // Fix PDP back button (in header) to return to the last collection
    if (lastCollectionUrl) {
      const backBtn = document.querySelector('[data-pdp-back]');
      if (backBtn) {
        const lastUrl = new URL(lastCollectionUrl, window.location.origin);
        backBtn.setAttribute('href', lastUrl.pathname + lastUrl.search);
      }
    }

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  /**
   * Update active nav item based on current URL
   */
  function updateActiveNav(path) {
    document.querySelectorAll('.header__nav-item').forEach(item => {
      const navUrl = item.getAttribute('data-nav-url');
      if (!navUrl) return;
      const isActive = path === navUrl || path.startsWith(navUrl + '/') || path === navUrl + '/';
      item.classList.toggle('header__nav-item--active', isActive);
      const link = item.querySelector('.header__nav-link');
      if (link) {
        if (isActive) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Re-initialize custom elements and components after content swap
   */
  function reinitComponents() {
    // Re-initialize custom elements that were inserted via innerHTML.
    // innerHTML doesn't trigger connectedCallback for already-defined
    // custom elements, so we replace each one with a clone to force
    // the browser to run connectedCallback on the fresh node.
    document.querySelectorAll('product-carousel').forEach(el => {
      const clone = document.createElement('product-carousel');
      // Copy attributes
      for (const attr of el.attributes) {
        clone.setAttribute(attr.name, attr.value);
      }
      clone.innerHTML = el.innerHTML;
      el.replaceWith(clone);
    });

    const form = document.querySelector('product-form');
    if (form && !form._initialized) {
      const formEl = form.querySelector('form[data-product-form]');
      if (formEl) {
        formEl.addEventListener('submit', (e) => {
          e.preventDefault();
          const btn = formEl.querySelector('[data-add-to-cart]');
          if (btn && btn.hasAttribute('data-open-sizes')) return;
          // For single-variant products, handle submit
          if (btn && !btn.disabled) {
            btn.disabled = true;
            fetch(window.CRYIN.routes.cartAdd + '.js', {
              method: 'POST',
              body: new FormData(formEl)
            }).then(r => {
              if (r.ok) {
                btn.classList.add('is-rotating');
                setTimeout(() => {
                  btn.disabled = false;
                  btn.classList.remove('is-rotating');
                }, 1000);
                return fetch(window.CRYIN.routes.cart + '.js');
              }
            }).then(r => r && r.json()).then(cart => {
              if (cart) {
                document.querySelectorAll('[data-cart-count]').forEach(el => {
                  el.textContent = cart.item_count;
                });
              }
            }).catch(() => { btn.disabled = false; });
          }
        });
        form._initialized = true;
      }
    }

    // Re-init bottom sheet
    initBottomSheet();

    // Re-init snap scroll on PDP
    initSnapScroll();

    // Re-init infinite scroll on collection pages
    initInfiniteScroll();

    // Re-init grid toggle state
    const saved = localStorage.getItem('cryin-grid-density');
    if (saved === 'dense') {
      document.body.dataset.gridDensity = 'dense';
    }

    // Preserve dark mode state
    const themeState = localStorage.getItem('cryin-theme');
    if (themeState === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    }

    // Re-init theme toggle in header (header persists across SPA nav)
    // No action needed since header is outside <main>

    // Re-run Shopify section rendering scripts
    if (window.Shopify && window.Shopify.designMode) return;
  }

  /**
   * Initialize inline size selector on PDP (Yeezy style)
   * Toggles between default view (title/price/+) and size selector view
   */
  function initBottomSheet() {
    const infoArea = document.querySelector('[data-pdp-info]');
    if (!infoArea) return;

    const defaultView = infoArea.querySelector('[data-info-default]');
    const sizesView = infoArea.querySelector('[data-info-sizes]');
    const openBtn = infoArea.querySelector('[data-open-sizes]');

    if (!defaultView || !sizesView || !openBtn) return;

    const closeBtn = sizesView.querySelector('[data-sizes-close]');
    const sizeBtns = sizesView.querySelectorAll('[data-variant-id]');
    const variantInput = infoArea.querySelector('[data-variant-id-input]');

    function showSizes() {
      // Animate + button rotating to ×
      if (openBtn) {
        openBtn.classList.add('is-rotating');
      }
      // Small delay for the + rotation, then swap views
      setTimeout(() => {
        defaultView.setAttribute('aria-hidden', 'true');
        sizesView.setAttribute('aria-hidden', 'false');
        if (openBtn) openBtn.classList.remove('is-rotating');
      }, 150);
    }

    function showDefault() {
      sizesView.setAttribute('aria-hidden', 'true');
      defaultView.setAttribute('aria-hidden', 'false');
      // Reset all sub-states: show sizes body, hide info body + size guide
      const sizesBody = sizesView.querySelector('[data-sizes-body]');
      const infoBody = sizesView.querySelector('[data-info-body]');
      const sizeGuide = sizesView.querySelector('[data-size-guide]');
      if (sizesBody) sizesBody.removeAttribute('aria-hidden');
      if (infoBody) infoBody.setAttribute('aria-hidden', 'true');
      if (sizeGuide) sizeGuide.setAttribute('aria-hidden', 'true');
      infoArea.classList.remove('is-scrollable');
      infoArea.scrollTop = 0;
    }

    openBtn.addEventListener('click', (e) => {
      e.preventDefault();
      showSizes();
    });

    // Close button is handled below after info toggle setup

    // Size buttons — add to cart with visual feedback
    const sizesTitle = sizesView.querySelector('.pdp__sizes-title');
    sizeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const variantId = btn.dataset.variantId;
        if (variantInput) variantInput.value = variantId;

        sizeBtns.forEach(b => b.classList.remove('pdp__size-btn--selected'));
        btn.classList.add('pdp__size-btn--selected');

        // Flash the selected button
        btn.classList.add('pdp__size-btn--added');
        btn.addEventListener('animationend', () => btn.classList.remove('pdp__size-btn--added'), { once: true });

        // Animated title text swap — strings from locale
        const strs = (window.CRYIN && window.CRYIN.strings) || {};
        const sStr = {
          sizeSelect: (strs.sizeSelect || 'SELECT SIZE').toUpperCase(),
          adding: (strs.adding || 'ADDING...').toUpperCase(),
          added: (strs.added || 'ADDED').toUpperCase(),
          errorMsg: (strs.errorMsg || 'ERROR').toUpperCase()
        };
        if (sizesTitle) {
          sizesTitle.classList.add('is-updating');
          setTimeout(() => {
            sizesTitle.textContent = sStr.adding;
            sizesTitle.classList.remove('is-updating');
          }, 150);
        }

        // Add to cart
        fetch(window.CRYIN.routes.cartAdd + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
        }).then(r => {
          if (r.ok) {
            // Animated "ADDED" confirmation
            if (sizesTitle) {
              sizesTitle.classList.add('is-updating');
              setTimeout(() => {
                sizesTitle.textContent = sStr.added;
                sizesTitle.classList.remove('is-updating');
              }, 150);
            }

            // Fly ghost image to cart
            flyToCart();

            // Fetch cart to update counter
            return fetch(window.CRYIN.routes.cart + '.js');
          } else {
            if (sizesTitle) {
              sizesTitle.classList.add('is-updating');
              setTimeout(() => {
                sizesTitle.textContent = sStr.errorMsg;
                sizesTitle.classList.remove('is-updating');
              }, 150);
            }
            setTimeout(() => {
              if (sizesTitle) {
                sizesTitle.classList.add('is-updating');
                setTimeout(() => {
                  sizesTitle.textContent = sStr.sizeSelect;
                  sizesTitle.classList.remove('is-updating');
                }, 150);
              }
            }, 1200);
            return null;
          }
        }).then(r => r && r.json()).then(cart => {
          if (cart) {
            updateCartCount(cart.item_count);
          }
          // Return to default view after ghost animation completes
          setTimeout(() => {
            if (sizesTitle) {
              sizesTitle.classList.add('is-updating');
              setTimeout(() => {
                sizesTitle.textContent = sStr.sizeSelect;
                sizesTitle.classList.remove('is-updating');
              }, 150);
            }
            sizeBtns.forEach(b => b.classList.remove('pdp__size-btn--selected'));
            showDefault();
          }, 900);
        });
      });
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sizesView.getAttribute('aria-hidden') === 'false') {
        showDefault();
      }
    });

    // Information toggle — swap between sizes body and info body
    const infoToggle = sizesView.querySelector('[data-toggle-info]');
    const sizesBody = sizesView.querySelector('[data-sizes-body]');
    const infoBody = sizesView.querySelector('[data-info-body]');
    if (infoToggle && sizesBody && infoBody) {
      infoToggle.addEventListener('click', () => {
        // Show info body, hide sizes body
        sizesBody.setAttribute('aria-hidden', 'true');
        infoBody.setAttribute('aria-hidden', 'false');
        infoArea.classList.add('is-scrollable');
      });
    }

    // Ruler button — opens size guide sub-state
    const sizeGuideBtn = sizesView.querySelector('[data-show-size-guide]');
    const sizeGuide = sizesView.querySelector('[data-size-guide]');
    if (sizeGuideBtn && sizesBody && sizeGuide) {
      sizeGuideBtn.addEventListener('click', () => {
        sizesBody.setAttribute('aria-hidden', 'true');
        if (infoBody) infoBody.setAttribute('aria-hidden', 'true');
        sizeGuide.setAttribute('aria-hidden', 'false');
        infoArea.classList.add('is-scrollable');
      });
    }

    // × close button — navigate back through sub-states
    if (closeBtn) {
      closeBtn.removeEventListener('click', showDefault);
      closeBtn.addEventListener('click', () => {
        // If size guide is showing, go back to sizes
        if (sizeGuide && sizeGuide.getAttribute('aria-hidden') === 'false') {
          sizeGuide.setAttribute('aria-hidden', 'true');
          sizesBody.removeAttribute('aria-hidden');
          infoArea.classList.remove('is-scrollable');
          infoArea.scrollTop = 0;
        }
        // If info body is showing, go back to sizes
        else if (infoBody && infoBody.getAttribute('aria-hidden') === 'false') {
          infoBody.setAttribute('aria-hidden', 'true');
          sizesBody.removeAttribute('aria-hidden');
          infoArea.classList.remove('is-scrollable');
          infoArea.scrollTop = 0;
        }
        // Otherwise close entirely back to default
        else {
          showDefault();
        }
      });
    }
  }

  /**
   * Initialize TikTok-style vertical snap scroll on PDP
   * Detects when user scrolls to prev/next product panel and navigates
   */
  function initSnapScroll() {
    const viewport = document.querySelector('[data-pdp-viewport]');
    if (!viewport) return;

    const currentPanel = viewport.querySelector('[data-current-panel]');
    const prevPanel = viewport.querySelector('[data-prev-product]');
    const nextPanel = viewport.querySelector('[data-next-product]');

    if (!currentPanel) return;

    // Prefetch prev/next products immediately for instant navigation
    if (prevPanel) {
      const prevUrl = prevPanel.getAttribute('data-prev-product');
      if (prevUrl) fetchPage(prevUrl).catch(() => {});
    }
    if (nextPanel) {
      const nextUrl = nextPanel.getAttribute('data-next-product');
      if (nextUrl) fetchPage(nextUrl).catch(() => {});
    }

    // Scroll to current product on load (middle panel)
    // Use viewport.scrollTop instead of scrollIntoView to avoid
    // the window scrolling past the header.
    // Double-rAF ensures layout is complete before measuring.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        viewport.scrollTop = currentPanel.offsetTop;
        // Also ensure the window itself is at top (header visible)
        window.scrollTo({ top: 0, behavior: 'instant' });
      });
    });

    let navigating = false;
    let scrollTimeout = null;
    const panelHeight = viewport.clientHeight;
    const currentTop = currentPanel.offsetTop;

    function handleScrollSnap() {
      if (navigating) return;

      const st = viewport.scrollTop;

      // Check if scrolled to prev panel (scrolled up past threshold)
      if (prevPanel && st < currentTop - panelHeight * 0.3) {
        navigating = true;
        const url = prevPanel.getAttribute('data-prev-product');
        if (url) navigateTo(url, true, true);
        return;
      }

      // Check if scrolled to next panel (scrolled down past threshold)
      if (nextPanel && st > currentTop + panelHeight * 0.3) {
        navigating = true;
        const url = nextPanel.getAttribute('data-next-product');
        if (url) navigateTo(url, true, true);
        return;
      }
    }

    // Use scrollend when available (most reliable)
    if ('onscrollend' in window) {
      viewport.addEventListener('scrollend', handleScrollSnap);
    }

    // Also use scroll with short debounce as fallback / additional trigger
    viewport.addEventListener('scroll', () => {
      if (navigating) return;
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScrollSnap, 80);
    }, { passive: true });
  }

  /**
   * Navigate to a URL with View Transition
   * @param {string} url
   * @param {boolean} isPushState - push to history
   * @param {boolean} skipTransition - skip view transition (for instant swaps like snap scroll)
   */
  async function navigateTo(url, isPushState = true, skipTransition = false) {
    try {
      // Update URL first so active nav detection works immediately
      if (isPushState) {
        history.pushState({ url }, '', url);
      }

      // Update active nav immediately (before content swap)
      const parsed = new URL(url, window.location.origin);
      updateActiveNav(parsed.pathname);

      // Pre-fetch if not cached
      const data = await fetchPage(url);

      // If navigating TO a product page, hide the footer instantly
      // before the View Transition captures its snapshot
      const isToProduct = data.bodyClass && data.bodyClass.includes('template-product');
      const isFromProduct = document.body.classList.contains('template-product');
      const footer = document.querySelector('.section-footer');

      if (footer && isToProduct && !isFromProduct) {
        footer.style.display = 'none';
      } else if (footer && !isToProduct && isFromProduct) {
        footer.style.removeProperty('display');
      }

      if (!skipTransition && supportsViewTransitions) {
        const transition = document.startViewTransition(() => {
          swapContent(data);
        });
        await transition.finished;
        // Clean up cards-only class after transition completes
        document.documentElement.classList.remove('vt-cards-only');
      } else {
        swapContent(data);
        document.documentElement.classList.remove('vt-cards-only');
      }

    } catch (error) {
      // Fallback: normal navigation
      window.location.href = url;
    }
  }

  /**
   * Prefetch on hover for faster navigation
   */
  function prefetchOnHover(link) {
    const url = link.href;
    if (!pageCache.has(url)) {
      fetchPage(url).catch(() => {});
    }
  }

  /**
   * Track which product card was clicked for view-transition targeting
   */
  let clickedProductId = null;

  /**
   * Track the last collection URL so PDP back button returns to it
   */
  let lastCollectionUrl = null;

  function trackCollectionUrl() {
    const path = window.location.pathname;
    if (path.startsWith('/collections/') && !path.includes('/products/')) {
      lastCollectionUrl = window.location.href;
    } else if (path === '/' || path === '') {
      lastCollectionUrl = window.location.href;
    }
  }

  // Track on initial page load
  trackCollectionUrl();

  /**
   * Intercept clicks on internal links
   */
  document.addEventListener('click', (e) => {
    const link = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (!link) return;

    // Skip if modifier keys (open in new tab)
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    // Skip if target blank
    if (link.target === '_blank') return;

    const url = link.href;
    if (!isInternalLink(url)) return;

    // Don't animate navigation to the same page
    const parsed = new URL(url, window.location.origin);
    if (parsed.pathname === window.location.pathname && parsed.search === window.location.search) return;

    e.preventDefault();

    // Navigation modes:
    // 1. Product card → PDP: view transition with product-hero zoom
    // 2. PDP back → grid: view transition with product-hero zoom
    // 3. Collection ↔ collection / nav: view transition with cards-only mode (products reposition)
    // 4. Snap scroll: no transition (instant)

    let navMode = 'cards'; // default: collection nav with card repositioning

    // Product card click → PDP (zoom in)
    const productCard = link.closest('.product-card');
    if (productCard) {
      navMode = 'product-zoom';
      // Clear card transition names to avoid ghosting
      document.querySelectorAll('.product-card').forEach(card => {
        card.style.viewTransitionName = 'none';
      });
      const media = productCard.querySelector('.product-card__media[data-product-id]');
      if (media) {
        clickedProductId = media.dataset.productId;
        media.style.viewTransitionName = 'product-hero';
      }
    }

    // PDP back button → grid (zoom out)
    const backBtn = link.closest('.pdp__back');
    if (backBtn) {
      navMode = 'product-zoom';
      const pdpSlide = document.querySelector('.pdp__slide--active[data-product-id]');
      if (pdpSlide) {
        clickedProductId = pdpSlide.dataset.productId;
        pdpSlide.style.viewTransitionName = 'product-hero';
      }
    }

    // For card-repositioning mode, add a CSS class so root transition is instant
    if (navMode === 'cards') {
      document.documentElement.classList.add('vt-cards-only');
    } else {
      document.documentElement.classList.remove('vt-cards-only');
    }

    navigateTo(url);
  });

  /**
   * Handle back/forward
   */
  window.addEventListener('popstate', (e) => {
    const url = window.location.href;
    // Use cards-only mode for back/forward between collections
    // (no product-zoom since we don't know which card was clicked)
    document.documentElement.classList.add('vt-cards-only');
    clickedProductId = null;
    navigateTo(url, false);
  });

  /**
   * Prefetch on hover/touch
   */
  document.addEventListener('pointerenter', (e) => {
    const link = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (link && isInternalLink(link.href)) {
      prefetchOnHover(link);
    }
  }, { passive: true, capture: true });

  /**
   * Initialize infinite scroll for collection grids
   * Uses IntersectionObserver to load next page when trigger enters viewport
   */
  function initInfiniteScroll() {
    const trigger = document.querySelector('[data-infinite-trigger]');
    if (!trigger) return;

    const grid = document.querySelector('[data-infinite-grid]');
    if (!grid) return;

    let loading = false;

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry.isIntersecting || loading) return;

      const nextUrl = trigger.getAttribute('data-next-url');
      if (!nextUrl) { observer.disconnect(); return; }

      loading = true;
      trigger.classList.add('is-loading');

      fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(r => r.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          // Append new product cards to grid
          const newGrid = doc.querySelector('[data-infinite-grid]');
          if (newGrid) {
            const cards = newGrid.querySelectorAll('.product-card');
            cards.forEach(card => {
              // Set view-transition-name for future animations
              grid.appendChild(card);
            });
          }

          // Check for next page
          const newTrigger = doc.querySelector('[data-infinite-trigger]');
          if (newTrigger) {
            trigger.setAttribute('data-next-url', newTrigger.getAttribute('data-next-url'));
          } else {
            // No more pages — remove trigger
            trigger.remove();
            observer.disconnect();
            return;
          }

          loading = false;
          trigger.classList.remove('is-loading');
        })
        .catch(() => {
          loading = false;
          trigger.classList.remove('is-loading');
        });
    }, {
      rootMargin: '400px' // Start loading 400px before trigger is visible
    });

    observer.observe(trigger);
  }

  // Initialize bottom sheet on first page load
  initBottomSheet();

  // Initialize snap scroll on first page load
  initSnapScroll();

  // Initialize infinite scroll on first page load
  initInfiniteScroll();

  // Expose flyToCart globally for component-product.js
  window.CRYIN = window.CRYIN || {};
  window.CRYIN.flyToCart = flyToCart;

})();
